import gql from 'graphql-tag';

export const PEOPLE_ROSTER_SHIFT_FRAGMENT = gql`
	fragment peopleRosterShift on RosterShift {
		id
		date
		startMinutes
		endMinutes
		area
		areaId
		areaLabel
		status
		statusLabel
		notes
		isSickDay
		sickReason
		publishedAt
		employee {
			id
			status
			firstName
			lastName
			preferredName
			personalEmail
			employmentType
			workDays
			position {
				id
				name
			}
			workTenant {
				id
				name
				timezone
			}
		}
	}
`;

export const PEOPLE_UNAVAILABILITY_FRAGMENT = gql`
	fragment peopleUnavailability on EmployeeUnavailability {
		id
		kind
		kindLabel
		leaveType
		leaveTypeLabel
		startDate
		endDate
		allDay
		startMinutes
		endMinutes
		notes
		batchId
		status
		statusLabel
		canDecide
		decidedAt
		decisionNotes
		automaticallyApproved
		created
		sourceRosterShift
		creator {
			id
			firstname
			lastname
		}
		decidedBy {
			id
			firstname
			lastname
		}
		employee {
			id
			status
			firstName
			lastName
			preferredName
			personalEmail
			employmentType
			workDays
			position {
				id
				name
			}
			workTenant {
				id
				name
			}
		}
	}
`;

export const PEOPLE_WORK_STATUS_FRAGMENT = gql`
	fragment peopleWorkStatus on EmployeeWorkStatus {
		id
		date
		area
		areaLabel
		displayLabel
		status
		statusLabel
		canDecide
		notes
		decisionNotes
		decidedAt
		automaticallyApproved
		employee {
			id
			status
			firstName
			lastName
			preferredName
			personalEmail
			employmentType
			workDays
			position {
				id
				name
			}
			workTenant {
				id
				name
			}
		}
		decidedBy {
			id
			firstname
			lastname
		}
	}
`;

export const GET_ROSTER_SHIFTS = gql`
	query($fromDate: String!, $toDate: String!, $filter: RosterShiftsFilter) {
		getRosterShifts(fromDate: $fromDate, toDate: $toDate, filter: $filter) {
			...peopleRosterShift
		}
	}
	${PEOPLE_ROSTER_SHIFT_FRAGMENT}
`;

export const GET_ROSTER_ACTUALS = gql`
	query($fromDate: String!, $toDate: String!, $filter: RosterActualsFilter) {
		getRosterActuals(fromDate: $fromDate, toDate: $toDate, filter: $filter) {
			date
			employee {
				id
				firstName
				lastName
				preferredName
				personalEmail
				workTenant {
					id
					name
					timezone
				}
			}
			timesheets {
				id
				date
				startMinutes
				endMinutes
				breakMinutes
				status
				statusLabel
				rosterShift {
					id
				}
				sourceClockInEvent {
					id
					action
					actionName
					capturedAt
					photoLink
				}
				sourceClockOutEvent {
					id
					action
					actionName
					capturedAt
					photoLink
				}
			}
			clockEvents {
				id
				action
				actionName
				modeName
				capturedAt
				kioskName
				photoLink
			}
		}
	}
`;

export const GET_EMPLOYEE_UNAVAILABILITIES = gql`
	query(
		$page: Int,
		$perPage: Int,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: EmployeeUnavailabilityFilter
	) {
		getEmployeeUnavailabilities(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...peopleUnavailability
			}
			count
		}
	}
	${PEOPLE_UNAVAILABILITY_FRAGMENT}
`;

export const GET_EMPLOYEE_WORK_STATUSES = gql`
	query($fromDate: String!, $toDate: String!, $filter: EmployeeWorkStatusFilter) {
		getEmployeeWorkStatuses(fromDate: $fromDate, toDate: $toDate, filter: $filter) {
			...peopleWorkStatus
		}
	}
	${PEOPLE_WORK_STATUS_FRAGMENT}
`;

export const GET_MY_ROSTER_SHIFTS = gql`
	query($fromDate: String!, $toDate: String!) {
		getMyRosterShifts(fromDate: $fromDate, toDate: $toDate) {
			id
			date
			startMinutes
			endMinutes
			area
			areaId
			areaLabel
			status
			notes
		}
	}
`;

export const GET_MY_UNAVAILABILITIES = gql`
	query($fromDate: String, $toDate: String) {
		getMyUnavailabilities(fromDate: $fromDate, toDate: $toDate) {
			id
			kind
			kindLabel
			leaveType
			leaveTypeLabel
			startDate
			endDate
			allDay
			startMinutes
			endMinutes
			notes
			status
			statusLabel
			decisionNotes
			automaticallyApproved
			created
		}
	}
`;

export const GET_MY_WORK_STATUSES = gql`
	query($fromDate: String, $toDate: String) {
		getMyWorkStatuses(fromDate: $fromDate, toDate: $toDate) {
			...peopleWorkStatus
		}
	}
	${PEOPLE_WORK_STATUS_FRAGMENT}
`;

export const CREATE_ROSTER_SHIFT = gql`
	mutation createRosterShift($input: CreateRosterShiftInput!) {
		createRosterShift(input: $input) {
			...peopleRosterShift
		}
	}
	${PEOPLE_ROSTER_SHIFT_FRAGMENT}
`;

export const MODIFY_ROSTER_SHIFT = gql`
	mutation modifyRosterShift($input: ModifyRosterShiftInput!) {
		modifyRosterShift(input: $input) {
			...peopleRosterShift
		}
	}
	${PEOPLE_ROSTER_SHIFT_FRAGMENT}
`;

export const MARK_ROSTER_SHIFT_SICK = gql`
	mutation markRosterShiftSick($id: ID!, $reason: String, $startMinutes: Int, $endMinutes: Int) {
		markRosterShiftSick(id: $id, reason: $reason, startMinutes: $startMinutes, endMinutes: $endMinutes) {
			...peopleRosterShift
		}
	}
	${PEOPLE_ROSTER_SHIFT_FRAGMENT}
`;

export const UNMARK_ROSTER_SHIFT_SICK = gql`
	mutation unmarkRosterShiftSick($id: ID!) {
		unmarkRosterShiftSick(id: $id) {
			...peopleRosterShift
		}
	}
	${PEOPLE_ROSTER_SHIFT_FRAGMENT}
`;

export const DESTROY_ROSTER_SHIFT = gql`
	mutation destroyRosterShift($id: ID!) {
		destroyRosterShift(id: $id)
	}
`;

export const DESTROY_ROSTER_SHIFTS = gql`
	mutation destroyRosterShifts($input: DestroyRosterShiftsInput!) {
		destroyRosterShifts(input: $input) {
			destroyedCount
		}
	}
`;

export const PUBLISH_ROSTER_SHIFTS = gql`
	mutation publishRosterShifts($fromDate: String!, $toDate: String!, $workTenantId: ID, $area: Int, $areaId: ID, $employeeIds: [ID]) {
		publishRosterShifts(fromDate: $fromDate, toDate: $toDate, workTenantId: $workTenantId, area: $area, areaId: $areaId, employeeIds: $employeeIds) {
			publishedCount
		}
	}
`;

export const COPY_ROSTER_SHIFTS = gql`
	mutation copyRosterShifts($fromDate: String!, $toDate: String!, $targetStartDate: String!, $workTenantId: ID, $area: Int, $areaId: ID, $employeeIds: [ID]) {
		copyRosterShifts(fromDate: $fromDate, toDate: $toDate, targetStartDate: $targetStartDate, workTenantId: $workTenantId, area: $area, areaId: $areaId, employeeIds: $employeeIds) {
			createdCount
			skippedCount
		}
	}
`;

export const REPEAT_ROSTER_SHIFT = gql`
	mutation repeatRosterShift($input: RepeatRosterShiftInput!) {
		repeatRosterShift(input: $input) {
			createdCount
			skippedCount
		}
	}
`;

export const REPEAT_EMPLOYEE_UNAVAILABILITY = gql`
	mutation repeatEmployeeUnavailability($input: RepeatEmployeeUnavailabilityInput!) {
		repeatEmployeeUnavailability(input: $input) {
			createdCount
			skippedCount
		}
	}
`;

export const REPEAT_MY_UNAVAILABILITY = gql`
	mutation repeatMyUnavailability($input: RepeatMyUnavailabilityInput!) {
		repeatMyUnavailability(input: $input) {
			createdCount
			skippedCount
		}
	}
`;

export const CREATE_EMPLOYEE_UNAVAILABILITY = gql`
	mutation createEmployeeUnavailability($input: CreateEmployeeUnavailabilityInput!) {
		createEmployeeUnavailability(input: $input) {
			...peopleUnavailability
		}
	}
	${PEOPLE_UNAVAILABILITY_FRAGMENT}
`;

export const MODIFY_EMPLOYEE_UNAVAILABILITY = gql`
	mutation modifyEmployeeUnavailability($input: ModifyEmployeeUnavailabilityInput!) {
		modifyEmployeeUnavailability(input: $input) {
			...peopleUnavailability
		}
	}
	${PEOPLE_UNAVAILABILITY_FRAGMENT}
`;

export const MODIFY_EMPLOYEE_UNAVAILABILITY_BATCH = gql`
	mutation modifyEmployeeUnavailabilityBatch($input: ModifyEmployeeUnavailabilityBatchInput!) {
		modifyEmployeeUnavailabilityBatch(input: $input) {
			modifiedCount
		}
	}
`;

export const DESTROY_EMPLOYEE_UNAVAILABILITY = gql`
	mutation destroyEmployeeUnavailability($id: ID!) {
		destroyEmployeeUnavailability(id: $id)
	}
`;

export const DESTROY_EMPLOYEE_UNAVAILABILITIES = gql`
	mutation destroyEmployeeUnavailabilities($input: DestroyEmployeeUnavailabilitiesInput!) {
		destroyEmployeeUnavailabilities(input: $input) {
			destroyedCount
		}
	}
`;

export const DECIDE_EMPLOYEE_UNAVAILABILITY = gql`
	mutation decideEmployeeUnavailability($id: ID!, $approve: Boolean!, $decisionNotes: String) {
		decideEmployeeUnavailability(id: $id, approve: $approve, decisionNotes: $decisionNotes) {
			...peopleUnavailability
		}
	}
	${PEOPLE_UNAVAILABILITY_FRAGMENT}
`;

export const SET_MY_WORK_STATUS = gql`
	mutation setMyWorkStatus($input: SetMyWorkStatusInput!) {
		setMyWorkStatus(input: $input) {
			...peopleWorkStatus
		}
	}
	${PEOPLE_WORK_STATUS_FRAGMENT}
`;

export const DESTROY_MY_WORK_STATUS = gql`
	mutation destroyMyWorkStatus($id: ID!) {
		destroyMyWorkStatus(id: $id)
	}
`;

export const DECIDE_EMPLOYEE_WORK_STATUS = gql`
	mutation decideEmployeeWorkStatus($id: ID!, $approve: Boolean!, $decisionNotes: String) {
		decideEmployeeWorkStatus(id: $id, approve: $approve, decisionNotes: $decisionNotes) {
			...peopleWorkStatus
		}
	}
	${PEOPLE_WORK_STATUS_FRAGMENT}
`;

export const CREATE_MY_UNAVAILABILITY = gql`
	mutation createMyUnavailability($input: CreateMyUnavailabilityInput!) {
		createMyUnavailability(input: $input) {
			id
			status
		}
	}
`;

export const MODIFY_MY_UNAVAILABILITY = gql`
	mutation modifyMyUnavailability($input: ModifyMyUnavailabilityInput!) {
		modifyMyUnavailability(input: $input) {
			id
			status
		}
	}
`;

export const DESTROY_MY_UNAVAILABILITY = gql`
	mutation destroyMyUnavailability($id: ID!) {
		destroyMyUnavailability(id: $id)
	}
`;

export const PEOPLE_ROSTER_OPEN_SHIFT_FRAGMENT = gql`
	fragment peopleRosterOpenShift on RosterOpenShift {
		id
		date
		startMinutes
		endMinutes
		area
		areaId
		areaLabel
		notes
		workTenant {
			id
			name
		}
	}
`;

export const GET_ROSTER_OPEN_SHIFTS = gql`
	query($fromDate: String!, $toDate: String!, $filter: RosterOpenShiftsFilter) {
		getRosterOpenShifts(fromDate: $fromDate, toDate: $toDate, filter: $filter) {
			...peopleRosterOpenShift
			applications {
				id
				status
				statusLabel
				decidedAt
				decisionNotes
				created
				employee {
					id
					status
					firstName
					lastName
					preferredName
					position {
						id
						name
					}
				}
			}
		}
	}
	${PEOPLE_ROSTER_OPEN_SHIFT_FRAGMENT}
`;

export const GET_MY_OPEN_SHIFTS = gql`
	query($fromDate: String!, $toDate: String!) {
		getMyOpenShifts(fromDate: $fromDate, toDate: $toDate) {
			id
			date
			startMinutes
			endMinutes
			area
			areaId
			areaLabel
			notes
			myApplication {
				id
				status
				statusLabel
				decisionNotes
			}
		}
	}
`;

export const CREATE_ROSTER_OPEN_SHIFT = gql`
	mutation createRosterOpenShift($input: CreateRosterOpenShiftInput!) {
		createRosterOpenShift(input: $input) {
			...peopleRosterOpenShift
		}
	}
	${PEOPLE_ROSTER_OPEN_SHIFT_FRAGMENT}
`;

export const MODIFY_ROSTER_OPEN_SHIFT = gql`
	mutation modifyRosterOpenShift($input: ModifyRosterOpenShiftInput!) {
		modifyRosterOpenShift(input: $input) {
			...peopleRosterOpenShift
		}
	}
	${PEOPLE_ROSTER_OPEN_SHIFT_FRAGMENT}
`;

export const DESTROY_ROSTER_OPEN_SHIFT = gql`
	mutation destroyRosterOpenShift($id: ID!) {
		destroyRosterOpenShift(id: $id)
	}
`;

export const APPLY_FOR_OPEN_SHIFT = gql`
	mutation applyForOpenShift($id: ID!) {
		applyForOpenShift(id: $id) {
			id
			status
			statusLabel
		}
	}
`;

export const WITHDRAW_MY_OPEN_SHIFT_APPLICATION = gql`
	mutation withdrawMyOpenShiftApplication($id: ID!) {
		withdrawMyOpenShiftApplication(id: $id)
	}
`;

export const DECIDE_OPEN_SHIFT_APPLICATION = gql`
	mutation decideOpenShiftApplication($id: ID!, $accept: Boolean!, $decisionNotes: String) {
		decideOpenShiftApplication(id: $id, accept: $accept, decisionNotes: $decisionNotes) {
			id
			status
			statusLabel
			decidedAt
			decisionNotes
		}
	}
`;
